import { CampaignTypeEnum, SystemNumberTypeEnum } from '@gr/shared/enums';
import { ISystemNumberListView } from '@gr/shared/models';
import { FastField, Formik } from 'formik';
import useClientsManual from '../../hooks/useClientsManual';
import { allEnabledClientsSearchOptions } from '../Clients/types';
import { Dropdown, IDropdownValue } from '../shared/Form/Dropdown';
import OldClientDropdown from '../shared/Form/Dropdowns/OldClientDropdown';
import ProviderDropdown from '../shared/Form/Dropdowns/ProviderDropdown';
import { ErrorMessage } from '../shared/Form/ErrorMessage';
import { TextInput } from '../shared/Form/TextInput';
import { ISystemNumberForm, campaignTypeDropdownValues, systemNumberFormSchema } from './types';

interface ITestNumberFormProps {
  item?: ISystemNumberListView;
  clientName?: string;
  providers: IDropdownValue[];
  tcrCampaignId?: string;
  onSubmit: (values: any) => void;
}

const SystemNumberForm = ({ item, clientName, providers, onSubmit }: ITestNumberFormProps): JSX.Element => {
  const initialFormState: ISystemNumberForm = {
    phoneNumber: item?.phoneNumber ?? undefined,
    providerId: item?.providerId ?? '',
    client: { value: item?.clientId ?? '', label: clientName ?? '' },
    campaignType: item?.campaignType ?? CampaignTypeEnum.FUNDRAISING,
    tcrCampaignId: item?.tcrCampaignId ?? ''
  };

  const isReadOnly: boolean = item !== null && item !== undefined;
  const [{ data: clientData, loading: clientLoading, error: clientError }, searchClients] =
    useClientsManual(allEnabledClientsSearchOptions);

  return (
    <>
      <Formik
        initialValues={initialFormState}
        validationSchema={systemNumberFormSchema}
        onSubmit={(values) => {
          onSubmit && onSubmit(values);
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched }) => (
          <>
            <form id="system-number-form" onSubmit={handleSubmit} className="h-full space-y-2">
              <FastField
                component={TextInput}
                id="phoneNumber"
                name="phoneNumber"
                label="Number"
                type="number"
                disabled={isReadOnly}
                value={values.phoneNumber}
                error={touched.phoneNumber ? errors.phoneNumber : ''}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <>
                <OldClientDropdown
                  value={values.client}
                  onChange={(newValue) => {
                    setFieldValue('client', newValue);
                    setFieldValue('providerId', '');
                  }}
                  onBlur={() => {
                    setFieldTouched('client');
                  }}
                  disabled={isReadOnly}
                />
                <ErrorMessage show={!!(touched.client && errors.client)} message={errors?.client?.label} />
              </>
              <>
                <ProviderDropdown
                  value={values.providerId ?? ''}
                  onChange={(newValue: IDropdownValue | undefined) => {
                    setFieldValue('providerId', newValue?.value ?? '');
                    setFieldValue('provider', newValue);
                  }}
                  onBlur={() => {
                    setFieldTouched('providerId', true);
                    setFieldTouched('provider', true);
                  }}
                  showError={!!(touched.providerId && errors.providerId)}
                  errorMessage={errors?.providerId}
                  tollFree={item?.type === SystemNumberTypeEnum.TOLL_FREE || !item}
                  disabled={isReadOnly}
                />
              </>

              <Dropdown
                label="Campaign Type"
                value={
                  values.campaignType ? campaignTypeDropdownValues.find((ct) => ct.value === values.campaignType) : null
                }
                onChange={(newValue: IDropdownValue) => {
                  setFieldValue('campaignType', newValue.value);
                }}
                onBlur={handleBlur}
                options={campaignTypeDropdownValues}
                disabled={isReadOnly || campaignTypeDropdownValues.length === 0}
                placeholder="Select a Campaign Type"
                error={
                  touched && campaignTypeDropdownValues.length === 0 && values.client.value
                    ? 'Client has no number for Provider'
                    : errors.campaignType
                }
              />
            </form>
          </>
        )}
      </Formik>
    </>
  );
};

export default SystemNumberForm;
